import { AgChartOptions, ColumnApi, GridApi } from "@ag-grid-community/core";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  GridItem,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { AgChartsReact } from "ag-charts-react";
import React from "react";
import { ColumnKeyEnum } from "../../constants";
import { IRegistryDataRow } from "../../models/RegistryData";
import { useTogglePaneOpen } from "../../routes/registries/stores/actions/useTogglePaneOpen";
import { useSelectIsPaneOpen } from "../../routes/registries/stores/select/useSelectIsPaneOpen";
import { useSelectRegistryId } from "../../routes/registries/stores/select/useSelectRegistryId";
import { getAgeChartOptions } from "../../routes/registries/util/ageChart";
import { getEthnicityChartOptions } from "../../routes/registries/util/ethnicityChart";
import { useReadRegistryData } from "../../services/api/data/queries/useReadRegistryData";
import { cloneCanvas, downloadCanvas } from "../../utils/canvas";
import { LibBoxDNASpinner } from "../lib";
import { MaterialIcon } from "../shared/MaterialIcon";

interface IProps {
  gridRef: any;
  isGridReady: boolean;
}

const RegistryPane = ({ gridRef, isGridReady }: IProps) => {
  const [caseCount, setCaseCount] = React.useState(0);
  const [isDisplayAgeColumn, setIsDisplayAgeColumn] = React.useState(false);
  const [isDisplayEthnicityColumn, setIsDisplayEthnicityColumn] =
    React.useState(false);
  const [isDisplayGenderColumn, setIsDisplayGenderColumn] =
    React.useState(false);
  const [ethnicityChartOptions, setEthnicityChartOptions] =
    React.useState<AgChartOptions>({});
  const [ageChartOptions, setAgeChartOptions] = React.useState<AgChartOptions>(
    {}
  );

  const isPaneOpen = useSelectIsPaneOpen();
  const registryId = useSelectRegistryId();
  const togglePaneOpen = useTogglePaneOpen();

  let { data: dataRegistry, isLoading: isLoadingRegistryData } =
    useReadRegistryData({
      registryId,
    });

  const handleOnViewColumn = (column_id) => {
    if (gridRef?.current?.columnApi) {
      gridRef.current.columnApi.setColumnsVisible([column_id], true);
    }
  };

  const handleOnTogglePane = () => {
    if (isPaneOpen) {
      togglePaneOpen();
    }
  };

  const generateAgeChart = React.useCallback(() => {
    const gridApi = gridRef?.current;
    let isDisplayAgeColumnUpdate = false;

    if (gridApi) {
      const column = gridApi.columnApi.getColumn(ColumnKeyEnum.age);

      if (column && column.isVisible()) {
        if (dataRegistry && dataRegistry.columns) {
          const ageColumn = dataRegistry?.columns.find(
            (col) => col.column_key === ColumnKeyEnum.age
          );

          if (ageColumn) {
            isDisplayAgeColumnUpdate = true;
            const gridApi = gridRef?.current?.api;

            if (gridApi) {
              let rowData: IRegistryDataRow[] = [];
              gridApi.forEachNodeAfterFilter((node) => rowData.push(node.data));
              const chartOptions = getAgeChartOptions(rowData, ageColumn);
              setAgeChartOptions(chartOptions);
            }
          }
        }
      }
    }

    setIsDisplayAgeColumn(isDisplayAgeColumnUpdate);
  }, [dataRegistry, gridRef]);

  const generateEthnicityChart = React.useCallback(() => {
    const gridApi = gridRef?.current;
    let isDisplayEthnicityColumnUpdate = false;

    if (gridApi) {
      const column = gridApi.columnApi.getColumn(ColumnKeyEnum.ethnicity);

      if (column && column.isVisible()) {
        if (dataRegistry && dataRegistry.columns) {
          const ethnicityColumn = dataRegistry?.columns.find(
            (col) => col.column_key === ColumnKeyEnum.ethnicity
          );

          if (ethnicityColumn) {
            isDisplayEthnicityColumnUpdate = true;
            const gridApi = gridRef?.current?.api;

            if (gridApi) {
              let rowData: IRegistryDataRow[] = [];
              gridApi.forEachNodeAfterFilter((node) => rowData.push(node.data));
              const chartOptions = getEthnicityChartOptions(
                rowData,
                ethnicityColumn
              );
              setEthnicityChartOptions(chartOptions);
            }
          }
        }
      }
    }
    setIsDisplayEthnicityColumn(isDisplayEthnicityColumnUpdate);
  }, [dataRegistry, gridRef]);

  const createGenderChart = React.useCallback(
    (gridApi: GridApi, columnApi: ColumnApi) => {
      const column = columnApi.getColumn(ColumnKeyEnum.sex_at_birth);
      let isDisplayGenderColumnUpdate = false;

      if (column && column.isVisible()) {
        isDisplayGenderColumnUpdate = true;

        const chartElem = document.getElementById("genderChart");
        if (chartElem) {
          chartElem.innerHTML = "";
        }

        gridApi.createCrossFilterChart({
          chartType: "doughnut",
          // chartType: "pie",
          cellRange: {
            columns: [ColumnKeyEnum.sex_at_birth, ColumnKeyEnum.record_id],
          },
          aggFunc: "count",
          chartThemeOverrides: {
            common: {
              title: {
                enabled: true,
                text: "Sex at Birth",
              },
              background: {
                opacity: 0,
              },
            },
            pie: {
              series: {
                strokeWidth: 2,
                shadow: {
                  enabled: true,
                },
                title: {
                  enabled: false,
                },
                label: {
                  enabled: true,
                },
              },
            },
          },
          chartContainer: chartElem as any,
        });
      }

      setIsDisplayGenderColumn(isDisplayGenderColumnUpdate);
    },
    []
  );

  const handleOnDownloadCanvas = (chartId, filename) => {
    if (!chartId) {
      return;
    }

    // Anchor to the chart ID element
    const chartidElem = document.querySelector(`#${chartId}`);

    if (chartidElem) {
      // Find the canvas html elem under the chart
      const canvasListElem = chartidElem.getElementsByTagName("canvas");

      if (canvasListElem && canvasListElem[0]) {
        // Make it bigger
        const canvasPrime = cloneCanvas(canvasListElem[0], 4);

        // Download
        if (canvasPrime) {
          downloadCanvas(canvasPrime, filename);
        }
      }
    }
  };

  const generateCaseCount = React.useCallback(() => {
    let setCaseCountUpdate = 0;

    if (gridRef?.current?.api) {
      setCaseCountUpdate = gridRef.current.api.getDisplayedRowCount();
    }

    setCaseCount(setCaseCountUpdate);
  }, [gridRef]);

  const onFilterChanged = React.useCallback(() => {
    generateCaseCount();
    generateEthnicityChart();
    generateAgeChart();
  }, [generateCaseCount, generateEthnicityChart, generateAgeChart]);

  const onDisplayedColumnsChanged = React.useCallback(() => {
    generateCaseCount();
    generateEthnicityChart();
    generateAgeChart();
    if (gridRef?.current?.api && isPaneOpen) {
      const gridApi = gridRef?.current?.api;
      const columnApi = gridRef?.current?.columnApi;
      createGenderChart(gridApi, columnApi);
    }
  }, [
    gridRef,
    generateCaseCount,
    generateEthnicityChart,
    generateAgeChart,
    createGenderChart,
    isPaneOpen,
  ]);

  const onFirstDataRendered = React.useCallback(() => {
    if (gridRef?.current?.api && isPaneOpen) {
      const gridApi = gridRef?.current?.api;
      const columnApi = gridRef?.current?.columnApi;

      createGenderChart(gridApi, columnApi);
      generateAgeChart();
      generateEthnicityChart();
      // createEegChart(gridApi);
      // createEegNormalChart(gridApi);
      // createAutismChart(gridApi);
    }
  }, [
    isPaneOpen,
    gridRef,
    createGenderChart,
    generateAgeChart,
    generateEthnicityChart,
  ]);

  React.useEffect(() => {
    if (isGridReady && isPaneOpen) {
      // get case count
      onFirstDataRendered();
    }
  }, [isGridReady, isPaneOpen, onFirstDataRendered]);

  React.useEffect(() => {
    generateCaseCount();
    generateEthnicityChart();
    generateAgeChart();
  }, [
    isGridReady,
    gridRef?.current?.api,
    generateAgeChart,
    generateCaseCount,
    generateEthnicityChart,
  ]);

  React.useEffect(() => {
    const gridApi = gridRef?.current?.api;
    if (gridApi) {
      gridApi.addEventListener("filterChanged", onFilterChanged);
      gridApi.addEventListener(
        "displayedColumnsChanged",
        onDisplayedColumnsChanged
      );
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener("filterChanged", onFilterChanged);
        gridApi.removeEventListener(
          "displayedColumnsChanged",
          onDisplayedColumnsChanged
        );
      }
    };
  }, [isGridReady, gridRef, onFilterChanged, onDisplayedColumnsChanged]);

  return (
    <Stack
      spacing={0}
      flex="1"
      overflow={"hidden"}
      borderTopWidth="1px"
      borderTopColor="border"
    >
      <HStack
        py={3}
        borderBottomWidth="1px"
        borderBottomColor="border"
        px={6}
        justifyContent="space-between"
      >
        <Text textStyle="header">Summary Statistics</Text>
        <ButtonGroup variant={"ghost"}>
          {/* <IconButton
            onClick={handleOnTogglePane}
            icon={
              <MaterialIcon
                name="file_download"
                fontSize="1rem"
                variant="outline"
              />
            }
            aria-label="download stats"
          ></IconButton>
          <IconButton
            onClick={handleOnTogglePane}
            icon={
              <MaterialIcon
                name="fullscreen"
                fontSize="1rem"
                variant="outline"
              />
            }
            aria-label="close menu"
          ></IconButton> */}
          <IconButton
            onClick={handleOnTogglePane}
            icon={
              <MaterialIcon
                name="keyboard_tab"
                fontSize="1rem"
                variant="outline"
              />
            }
            aria-label="close menu"
          ></IconButton>
        </ButtonGroup>
      </HStack>
      <Stack
        h="full"
        px={6}
        pt={4}
        flex={1}
        position="relative"
        overflowY={"auto"}
      >
        {isPaneOpen && !isGridReady && (
          <Center h="full">
            <LibBoxDNASpinner />
          </Center>
        )}

        {isPaneOpen && isGridReady && (
          <Box pb={12}>
            <Stack h="full" spacing={8}>
              <Box rounded="sm" borderWidth={1} borderColor="border" p={4}>
                <Stack spacing={1}>
                  <Text textStyle="header">Total Cases</Text>
                  <Text fontSize="3xl">{caseCount}</Text>
                </Stack>
              </Box>

              <SimpleGrid w="full" columns={{ base: 1, lg: 2 }} gap={6}>
                <GridItem
                  position="relative"
                  rounded="sm"
                  borderWidth={1}
                  borderColor="border"
                  colSpan={2}
                  py={4}
                >
                  {isDisplayAgeColumn &&
                    ageChartOptions &&
                    !isLoadingRegistryData && (
                      <Box id="ageChart">
                        <AgChartsReact options={ageChartOptions} />
                      </Box>
                    )}
                  {!isDisplayAgeColumn && (
                    <Stack
                      minH={24}
                      h="full"
                      spacing={6}
                      alignItems="center"
                      p={8}
                    >
                      <Center flex={1}>
                        <MaterialIcon
                          color="text.soft"
                          fontSize="2rem"
                          name="visibility_off"
                          variant="outline"
                        />
                      </Center>

                      <Stack spacing="2">
                        <Text fontSize="lg" textAlign={"center"}>
                          Age data not visible on graph
                        </Text>

                        <Center>
                          <Button
                            size="sm"
                            onClick={() =>
                              handleOnViewColumn(ColumnKeyEnum.age)
                            }
                          >
                            Show Age Column
                          </Button>
                        </Center>
                      </Stack>
                    </Stack>
                  )}
                  <Box pos={"absolute"} top="4" right={4} z-index="100">
                    <IconButton
                      size="sm"
                      variant={"outline"}
                      onClick={() =>
                        handleOnDownloadCanvas("ageChart", "geneial-age.png")
                      }
                      icon={
                        <MaterialIcon
                          name="file_download"
                          fontSize="1rem"
                          variant="outline"
                        />
                      }
                      aria-label="download"
                    ></IconButton>
                  </Box>
                </GridItem>

                <GridItem
                  position="relative"
                  rounded="sm"
                  borderWidth={1}
                  borderColor="border"
                  colSpan={2}
                  py={4}
                >
                  {isDisplayEthnicityColumn &&
                    ethnicityChartOptions &&
                    !isLoadingRegistryData && (
                      <Box id="ethnicityChart">
                        <AgChartsReact options={ethnicityChartOptions} />
                      </Box>
                    )}
                  {!isDisplayEthnicityColumn && (
                    <Stack
                      minH={24}
                      h="full"
                      spacing={6}
                      alignItems="center"
                      p={8}
                    >
                      <Center flex={1}>
                        <MaterialIcon
                          color="text.soft"
                          fontSize="2rem"
                          name="visibility_off"
                          variant="outline"
                        />
                      </Center>

                      <Stack spacing="2">
                        <Text fontSize="lg" textAlign={"center"}>
                          Ethnicty data not visible on graph
                        </Text>

                        <Center>
                          <Button
                            size="sm"
                            onClick={() =>
                              handleOnViewColumn(ColumnKeyEnum.ethnicity)
                            }
                          >
                            Show Ethnicity Column
                          </Button>
                        </Center>
                      </Stack>
                    </Stack>
                  )}
                  <Box pos={"absolute"} top="4" right={4}>
                    <IconButton
                      size="sm"
                      variant={"outline"}
                      onClick={() =>
                        handleOnDownloadCanvas(
                          "ethnicityChart",
                          "geneial-ethnicity-graph.png"
                        )
                      }
                      icon={
                        <MaterialIcon
                          name="file_download"
                          fontSize="1rem"
                          variant="outline"
                        />
                      }
                      aria-label="download"
                    ></IconButton>
                  </Box>
                </GridItem>

                <GridItem
                  position="relative"
                  rounded="sm"
                  borderWidth={1}
                  borderColor="border"
                  colSpan={2}
                  py={4}
                >
                  <Box
                    h={isDisplayGenderColumn ? "full" : 0}
                    id="genderChart"
                  ></Box>

                  {!isDisplayGenderColumn && (
                    <Stack
                      minH={24}
                      h="full"
                      spacing={6}
                      alignItems="center"
                      p={8}
                    >
                      <Center flex={1}>
                        <MaterialIcon
                          color="text.soft"
                          fontSize="2rem"
                          name="visibility_off"
                          variant="outline"
                        />
                      </Center>

                      <Stack spacing="1">
                        <Text fontSize="lg" textAlign={"center"}>
                          Sex at birth data not visible on graph
                        </Text>

                        <Center>
                          <Button
                            size="sm"
                            onClick={() =>
                              handleOnViewColumn(ColumnKeyEnum.sex_at_birth)
                            }
                          >
                            Show Sex at Birth Column
                          </Button>
                        </Center>
                      </Stack>
                    </Stack>
                  )}
                  <Box pos={"absolute"} top="4" right={4} z-index="100">
                    <IconButton
                      size="sm"
                      variant={"outline"}
                      onClick={() =>
                        handleOnDownloadCanvas(
                          "genderChart",
                          "geneial-sex-at-birth.png"
                        )
                      }
                      icon={
                        <MaterialIcon
                          name="file_download"
                          fontSize="1rem"
                          variant="outline"
                        />
                      }
                      aria-label="download"
                    ></IconButton>
                  </Box>
                </GridItem>
              </SimpleGrid>

              {/* <Box
                rounded="sm"
                borderWidth={1}
                borderColor="border"
                p={4}
                mb={8}
              >
                <Stack spacing={1}>
                  <Text textStyle="header">Geneial tips:</Text>
                  <HStack justify={"space-between"}>
                    <Text>Add multiple filters</Text>
                    <span>
                      Hold <Kbd>ctrl/cmd</Kbd> + <Kbd>shift</Kbd> and click
                      graph
                    </span>
                  </HStack>
                  <HStack justify={"space-between"}>
                    <Text>Reset filters</Text>
                    <span>Click outside any graph</span>
                  </HStack>
                </Stack>
              </Box> */}
            </Stack>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default RegistryPane;
